import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/lib/lang";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { lang, toggleLang, t } = useLang();

  const navLinks = [
    { label: t("nav_about"), href: "#about" },
    { label: t("nav_apps"), href: "#apps" },
    { label: t("nav_services"), href: "/services", isRoute: true },
    { label: t("nav_marketplace"), href: "/marketplace", isRoute: true },
    { label: t("nav_contact"), href: "#contact" },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#05050A]/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00F0FF] to-[#4D00FF] flex items-center justify-center text-black font-black text-lg font-['Unbounded']">F</div>
          <span className="font-['Unbounded'] font-bold text-white text-lg tracking-tight hidden sm:block">Fortis Digital</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link key={link.label} to={link.href} data-testid={`nav-${link.label.toLowerCase()}`}
                className="text-sm font-medium text-[#A0A0AB] hover:text-[#00F0FF] transition-colors duration-300 tracking-wide">
                {link.label}
              </Link>
            ) : (
              <a key={link.label} href={link.href} onClick={(e) => handleNavClick(e, link.href)}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className="text-sm font-medium text-[#A0A0AB] hover:text-[#00F0FF] transition-colors duration-300 tracking-wide">
                {link.label}
              </a>
            )
          ))}
          {/* Language Toggle */}
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/15 text-xs font-['JetBrains_Mono'] font-bold text-white hover:border-[#00F0FF]/40 hover:text-[#00F0FF] transition-all"
          >
            <span className={lang === "en" ? "text-[#00F0FF]" : "text-[#A0A0AB]"}>EN</span>
            <span className="text-white/30">|</span>
            <span className={lang === "es" ? "text-[#00F0FF]" : "text-[#A0A0AB]"}>ES</span>
          </button>
          <Link to="/admin" data-testid="nav-admin" className="neon-btn text-xs py-2 px-5">
            {t("nav_admin")}
          </Link>
        </nav>

        <button data-testid="mobile-menu-toggle" className="md:hidden text-white" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0F0F1A]/95 backdrop-blur-xl border-b border-white/10">
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link key={link.label} to={link.href} onClick={() => setMobileOpen(false)}
                    className="text-base text-[#A0A0AB] hover:text-[#00F0FF] transition-colors">
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.label} href={link.href} onClick={(e) => handleNavClick(e, link.href)}
                    className="text-base text-[#A0A0AB] hover:text-[#00F0FF] transition-colors">
                    {link.label}
                  </a>
                )
              ))}
              <button onClick={toggleLang} className="flex items-center gap-2 text-base text-[#A0A0AB] hover:text-[#00F0FF] transition-colors">
                {lang === "en" ? "Cambiar a Español" : "Switch to English"}
              </button>
              <Link to="/admin" className="neon-btn text-center text-sm py-2 mt-2">{t("nav_admin")}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
